/**
 * Version 1 application state machine.
 *
 * One machine drives the whole journey. Every visible thing is a function of state,
 * so nothing can render a capability the app does not have (ENG-037).
 */
import { report } from './report';

export type V1State =
  | 'BOOTING'        // modelled, not presented — poster paints, scene builds
  | 'STARTUP'        // CC_TRANSITION_001 playing over the pre-built scene
  | 'ARRIVED'        // handoff done; ambient beat, no prompt yet
  | 'IDLE_PROMPT'    // DROP AUDIO HERE revealed
  | 'ACCEPTING'      // file received, decoding
  | 'WAKING'         // CC_CRT_001_POWER_BLOOM
  | 'LOCKING'        // CC_CRT_002_SIGNAL_LOCK
  | 'REALM'          // Channel One live, knob interactive
  | 'FAILED';        // truthful failure state

export interface Ctx {
  readonly state: V1State;
  /** Cached last-known display name, for truthful failure reporting. */
  readonly broadcastName: string;
  readonly failure: string | null;
  readonly drift: number;      // 0..1 hero knob value
  readonly fileName: string | null;
  readonly playing: boolean;
}

type Listener = (c: Ctx) => void;

const INITIAL: Ctx = {
  state: 'BOOTING',
  broadcastName: 'CHANNEL ONE',
  failure: null,
  drift: 0,
  fileName: null,
  playing: false,
};

/** Legal transitions. Anything absent here is a bug, not a feature. */
const ALLOWED: Record<V1State, readonly V1State[]> = {
  BOOTING: ['STARTUP', 'ARRIVED', 'FAILED'],
  STARTUP: ['ARRIVED', 'FAILED'],
  ARRIVED: ['IDLE_PROMPT', 'FAILED'],
  IDLE_PROMPT: ['ACCEPTING', 'FAILED'],
  ACCEPTING: ['WAKING', 'FAILED', 'IDLE_PROMPT'],
  WAKING: ['LOCKING', 'FAILED'],
  LOCKING: ['REALM', 'FAILED'],
  REALM: ['ACCEPTING', 'FAILED'],
  FAILED: ['IDLE_PROMPT', 'ACCEPTING'],
};

export class Machine {
  #ctx: Ctx = INITIAL;
  #listeners = new Set<Listener>();

  get ctx(): Ctx { return this.#ctx; }

  subscribe(fn: Listener): () => void {
    this.#listeners.add(fn);
    fn(this.#ctx);
    return () => { this.#listeners.delete(fn); };
  }

  /** State change. Rejects illegal transitions loudly in dev. */
  go(next: V1State, patch: Partial<Ctx> = {}): void {
    const from = this.#ctx.state;
    if (from !== next && !ALLOWED[from].includes(next)) {
      const msg = `illegal transition ${from} -> ${next}`;
      if (import.meta.env.DEV) throw new Error(msg);
      report('machine', msg);
      return;
    }
    this.#emit({ ...patch, state: next });
  }

  /** Value change without a state change (knob movement). */
  patch(patch: Partial<Omit<Ctx, 'state'>>): void {
    this.#emit(patch);
  }

  fail(reason: string): void {
    this.go('FAILED', { failure: reason });
  }

  #emit(patch: Partial<Ctx>): void {
    this.#ctx = { ...this.#ctx, ...patch };
    for (const fn of this.#listeners) fn(this.#ctx);
  }
}
